import { Icon } from '../Icon.js';
import { formatBytes } from '../references/format.js';
import type { QueuedUpload } from './useUploadQueue.js';

/**
 * ONE FILE OF A BULK UPLOAD: its name, its size, a byte progress bar while it is
 * sending, and where it stands — Waiting, the percentage, the done word, or the
 * server's reason it failed. `removable` offers the ✕ for a file not yet sent.
 *
 * Drawn by the References library's drawer (`ref-add-*` test ids) and the Files
 * tab's upload sheet (`files-upload-*`), with the same styles (`.ref-upload-*`).
 */
export function UploadRow<M>({
  item,
  removable,
  onRemove,
  doneLabel,
  testIdPrefix,
}: {
  item: QueuedUpload<M>;
  removable: boolean;
  onRemove: () => void;
  doneLabel: string;
  testIdPrefix: string;
}) {
  const percent = item.total > 0 ? Math.min(100, Math.round((item.loaded / item.total) * 100)) : 0;
  return (
    <li className="ref-upload-item" data-state={item.state} data-testid={`${testIdPrefix}-file-${item.file.name}`}>
      <span className="ref-upload-name">{item.file.name}</span>
      <span className="ref-upload-size">{formatBytes(item.file.size)}</span>
      {item.state === 'uploading' ? (
        <span
          className="ref-progress"
          role="progressbar"
          aria-label={`Uploading ${item.file.name}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          data-testid={`${testIdPrefix}-progress-${item.file.name}`}
        >
          <span style={{ width: `${String(percent)}%` }} />
        </span>
      ) : null}
      <span
        className="ref-upload-status"
        data-tone={item.state === 'failed' ? 'danger' : item.state === 'done' ? 'ok' : undefined}
        data-testid={`${testIdPrefix}-status-${item.file.name}`}
      >
        {item.state === 'queued'
          ? 'Waiting'
          : item.state === 'uploading'
            ? `${String(percent)}%`
            : item.state === 'done'
              ? doneLabel
              : (item.error ?? 'Failed')}
      </span>
      {removable ? (
        <button
          type="button"
          className="ref-chip-x"
          aria-label={`Remove ${item.file.name}`}
          data-testid={`${testIdPrefix}-remove-${item.file.name}`}
          onClick={onRemove}
        >
          <Icon name="close" size={12} />
        </button>
      ) : null}
    </li>
  );
}
